import CloseIcon from '@mui/icons-material/Close';
import { Box, IconButton, Paper, Popper, Typography } from '@mui/material';
import { useCallback, useState } from 'react';

import { SURFACE_RADIUS } from '~/theme';

export const OTTO_INTRO_DISMISSED_KEY = 'otto_intro_dismissed';

export const OTTO_INTRO_COPY =
  "Ask me anything across your tickets, callbacks, invoices, contracts, properties and compliance. I'll pull it together.";

/**
 * One-shot "seen it" marker for the intro tip, held in localStorage for cross-session
 * memory and in state for in-session reactivity. `logout()` clears localStorage
 * wholesale, so this is once per signed-in session-chain rather than once per user.
 */
export function useOttoIntroDismissal(): { dismissed: boolean; dismiss: () => void } {
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(OTTO_INTRO_DISMISSED_KEY) === 'true',
  );

  const dismiss = useCallback(() => {
    localStorage.setItem(OTTO_INTRO_DISMISSED_KEY, 'true');
    setDismissed(true);
  }, []);

  return { dismissed, dismiss };
}

/**
 * First-run tip under the "Ask Lift AI" button, saying what the assistant can answer.
 *
 * A `Popper` rather than a `Popover`: `Popover` lays an invisible backdrop over the
 * page that eats the first click anywhere, and this appears unprompted.
 */
export function OttoIntroTip({
  anchorEl,
  open,
  onDismiss,
}: {
  anchorEl: HTMLElement | null;
  open: boolean;
  onDismiss: () => void;
}) {
  const isOpen = open && anchorEl !== null;
  // Popper positions the caret horizontally, so it has to be a real element it
  // can measure rather than a pseudo-element on the paper.
  const [caretEl, setCaretEl] = useState<HTMLElement | null>(null);

  return (
    <Popper
      open={isOpen}
      anchorEl={anchorEl}
      placement="bottom-end"
      modifiers={[
        { name: 'offset', options: { offset: [0, 6] } },
        { name: 'arrow', options: { element: caretEl, padding: 12 } },
      ]}
      // Over the header it points at. The app bar is `zIndex.drawer + 1` (1201), not
      // `zIndex.appBar`, and the caret is the part that reaches into the bar. Still under
      // the mobile drawer and modals.
      sx={{ zIndex: (theme) => theme.zIndex.drawer + 2 }}
    >
      <Paper
        elevation={6}
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          p: 2,
          width: 'min(420px, calc(100vw - 32px))',
          borderRadius: `${SURFACE_RADIUS}px`,
        }}
      >
        {/* Caret pointing up at the button. The square is rotated on a child so Popper stays
            free to write its own transform on the element it positions. Its two upper edges
            are outlined: a plain white caret is invisible against the white header. */}
        <Box
          ref={setCaretEl}
          sx={{
            position: 'absolute',
            top: 0,
            width: 14,
            height: 14,
            mt: '-7px',
            '&::before': {
              content: '""',
              display: 'block',
              width: '100%',
              height: '100%',
              bgcolor: 'background.paper',
              borderTop: '1px solid',
              borderLeft: '1px solid',
              borderColor: 'divider',
              transform: 'rotate(45deg)',
            },
          }}
        />
        <Typography variant="body2" color="text.secondary">
          {OTTO_INTRO_COPY}
        </Typography>
        <IconButton
          size="small"
          aria-label="Dismiss assistant tip"
          onClick={onDismiss}
          sx={{ mt: -0.5, mr: -0.5, flexShrink: 0 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Paper>
    </Popper>
  );
}
